/**
 * ImportCustomerBaseView — Item 391-C · tela "Importar base de clientes" (admin).
 *
 * Fluxo em etapas numa única página: upload → mapeamento → preview → importar →
 * painel de resultado. Carrega a base do tenant (telefone, titular, CPF/CNPJ,
 * nº de contrato, papel) para a Lume; o backend faz o upsert idempotente,
 * promove Identities observed→imported e gera pendências de reconciliação quando
 * a base diverge. É o que destrava o match de posse de canal (Fatia 2).
 *
 * Gate de cap (`customer-registry-import`) é aplicado pela Page que envolve esta
 * View; aqui assumimos a cap concedida.
 */

import { useMemo, useState } from 'react';

import { suggestMapping } from './csv-parser';
import { MappingStep, buildMapping } from './MappingStep';
import { PreviewTable } from './PreviewTable';
import { ResultPanel } from './ResultPanel';
import { UploadStep } from './UploadStep';
import { useImportCustomerBase } from './use-import-customer-base';

import type { ParsedCsv } from './csv-parser';
import type { MappingDraft } from './MappingStep';
import type { ImportDefaultChannel } from '../../lib/types';

const EMPTY_DRAFT: MappingDraft = {
  phone: '',
  holderName: '',
  cpfCnpj: '',
  contractNumber: '',
  role: '',
};

export function ImportCustomerBaseView(): JSX.Element {
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [draft, setDraft] = useState<MappingDraft>(EMPTY_DRAFT);
  const [defaultChannel, setDefaultChannel] = useState<ImportDefaultChannel>('whatsapp_cloud_api');

  const { importBase, isPending, result, reset } = useImportCustomerBase();

  const handleParsed = (name: string, result: ParsedCsv): void => {
    setFileName(name);
    setParsed(result);
    // Auto-sugere o mapeamento a partir dos cabeçalhos.
    const suggested = suggestMapping(result.headers);
    setDraft({
      phone: suggested.phone ?? '',
      holderName: suggested.holderName ?? '',
      cpfCnpj: suggested.cpfCnpj ?? '',
      contractNumber: suggested.contractNumber ?? '',
      role: suggested.role ?? '',
    });
  };

  const canImport = useMemo(
    () => parsed !== null && parsed.rows.length > 0 && draft.phone !== '',
    [parsed, draft.phone],
  );

  const handleImport = async (): Promise<void> => {
    if (parsed === null || draft.phone === '') return;
    await importBase({
      rows: parsed.rows,
      mapping: buildMapping(draft),
      defaultChannel,
    });
  };

  const handleReset = (): void => {
    setFileName(null);
    setParsed(null);
    setDraft(EMPTY_DRAFT);
    setDefaultChannel('whatsapp_cloud_api');
    reset();
  };

  return (
    <div className="cr-screen">
      <header className="cr-pagehead">
        <div className="cr-pagehead__crumbs">Configuração</div>
        <h1 className="cr-pagehead__title">Importar base de clientes</h1>
        <p className="cr-pagehead__sub">
          Carregue sua base (telefone, titular, CPF/CNPJ, nº de contrato, papel). A Lume registra
          cada cliente como origem autoritativa — sem duplicar quem já conversou — e abre pendências
          de reconciliação quando os dados divergem da base existente.
        </p>
      </header>

      {result !== undefined ? (
        <ResultPanel result={result} onReset={handleReset} />
      ) : (
        <>
          <UploadStep fileName={fileName} parsed={parsed} onParsed={handleParsed} />

          {parsed !== null && (
            <>
              <MappingStep
                headers={parsed.headers}
                draft={draft}
                defaultChannel={defaultChannel}
                onChange={setDraft}
                onChannelChange={setDefaultChannel}
              />

              <PreviewTable rows={parsed.rows} draft={draft} />

              <div className="cr-actions">
                <button
                  type="button"
                  className="cr-btn cr-btn--primary"
                  disabled={!canImport || isPending}
                  onClick={() => void handleImport()}
                  data-testid="cr-import-btn"
                >
                  {isPending ? 'Importando…' : `Importar ${parsed.rows.length} linha(s)`}
                </button>
                {!canImport && (
                  <span className="cr-upload__hint">
                    Mapeie a coluna de <strong>Telefone</strong> para habilitar a importação.
                  </span>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
